import { useContext } from "react";
import { RecordsHeader } from "../../components/recordsHeader/RecordsHeader";
import { RecordsContext } from "../../components/records/Records";
import { Box, Typography, IconButton, LinearProgress } from "@mui/material";
import { useRecords } from "../../api/useRecords";
import { formatCurrency } from "../../utils/formatCurrency";
import { ORDERS_SINCE_DATE } from "../../utils/constants";
import { calculateTotalOfOrdersFromPayments, GroupedOrdersTotalsByPaymentMethod } from "./utils";
import { getTodayOrdersGroupedByPaymentMethod } from "./utils";
import { findField } from "../../utils/findField";
import { FormFields } from "../../components/entityForm/EntityForm";
import { useForm } from "react-hook-form";
import { UseFormReturn } from "react-hook-form";
import { sortBy } from "lodash";
import { ScheduleSend, Refresh } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useStore } from "../../store/store";
import { getClinicFromFilter } from "../../utils/getClinicFromFilter";
import { format, startOfDay, addDays } from "date-fns";

export const OrdersListHeader = () => {
    const recordsContext = useContext(RecordsContext);
    const entityForm = useForm<FormFields>();
    const { recordsFilter } = useStore();
    const clinicFiltered = getClinicFromFilter(recordsFilter, "orders");
    const clinicFilter = clinicFiltered !== -1 ? { clinic: { id: { _eq: clinicFiltered } } } : {};

    const todayStart = format(startOfDay(new Date()), "yyyy-MM-dd HH:mm:ss");
    const tomorrowStart = format(startOfDay(addDays(new Date(), 1)), "yyyy-MM-dd HH:mm:ss");

    const { data: todayPaidOrders, status: todayPaidOrdersStatus } = useRecords(
        "orders",
        "id, payment_method, order_items.price, order_items.quantity, order_items.discount, orders_payments.*",
        {
            extraOptions: {
                filter: {
                    _and: [
                        { orders_payments: { payment_date: { _gte: todayStart } } },
                        { orders_payments: { payment_date: { _lt: tomorrowStart } } },
                        clinicFilter,
                    ],
                },
                pageSize: -1,
            },
        }
    );

    const { data: moneyOrders, status: moneyOrdersStatus } = useRecords(
        "orders",
        "id, order_items.price, order_items.quantity, order_items.discount, orders_payments.*",
        {
            extraOptions: {
                filter: {
                    _and: [
                        { orders_payments: { payment_method: { _eq: "cash" } } },
                        { orders_payments: { connected_bank_transaction_id: { _null: true } } },
                        { order_date: { _gte: ORDERS_SINCE_DATE } },
                        clinicFilter,
                    ],
                },
                pageSize: -1,
            },
        }
    );

    if (
        !recordsContext ||
        moneyOrdersStatus !== "success" ||
        todayPaidOrdersStatus !== "success"
    ) {
        return <LinearProgress color="secondary" />;
    }

    const paymentMethodField = findField(recordsContext.formatedFields, "payment_method");
    const groupedTodayOrders = getTodayOrdersGroupedByPaymentMethod(todayPaidOrders.items, paymentMethodField);
    const moneyOrdersTotal = calculateTotalOfOrdersFromPayments(moneyOrders?.items, "cash");
  

    return (
        <Box>
            <RecordsHeader
                collection={recordsContext?.collection}
                optionalComponents={{
                    bellowTitle: <BellowTitleOrdersList />,
                    leftOfButtons: (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                gap: 8,
                                mr: 6,
                                flexWrap: { xs: "wrap", md: "nowrap" },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexDirection: "column" }}>
                                <Typography variant="body2" color="primary">
                                    Resumo do dia de hoje:
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 3, justifyContent: "center" }}>
                                <PaymentMethodTotals groupedOrders={groupedTodayOrders} entityForm={entityForm} />
                                <PocketMoney totalAmount={moneyOrdersTotal} />
                            </Box>
                        </Box>
                    ),
                }}
            />
        </Box>
    );
};

type PocketMoneyProps = {
    totalAmount: number;
};

const PocketMoney = ({ totalAmount }: PocketMoneyProps) => {
    return (
        <Box sx={{ maxWidth: "100px" }}>
            <Typography variant="body1" fontWeight={600} sx={{ textAlign: "center" }}>
                {formatCurrency(totalAmount)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: "center" }}>
                Fundo de
                <br />
                Caixa
            </Typography>
        </Box>
    );
};

type PaymentMethodTotalsProps = {
    groupedOrders: GroupedOrdersTotalsByPaymentMethod;
    entityForm: UseFormReturn<FormFields>;
};

const PaymentMethodTotals = ({ groupedOrders }: PaymentMethodTotalsProps) => {
    const sortedEntries = sortBy(Object.entries(groupedOrders), ([paymentMethod]) => paymentMethod);

    return (
        <Box sx={{ display: "flex", gap: 3 }}>
            {sortedEntries.map(([paymentMethod, total]) => (
                <Box key={paymentMethod} sx={{ display: "flex", flexDirection: "column", maxWidth: "100px" }}>
                    <Typography variant="body1" fontWeight={600} sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                        {formatCurrency(Number(total))}
                    </Typography>
                    <Typography variant="body2" sx={{ minHeight: "2.86em", lineHeight: 1.43, textAlign: "center" }}>
                        {paymentMethod}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

export const BellowTitleOrdersList = () => {
    const queryClient = useQueryClient();

    const handleRefreshInvoices = () => {
        queryClient.invalidateQueries({ queryKey: ["order-invoice"] });
    };

    return (
        <Box display="flex" gap={0.5}>
            <IconButton component={Link} to="/scheduled_invoices" color="primary">
                <ScheduleSend />
            </IconButton>
            <IconButton onClick={handleRefreshInvoices} color="primary">
                <Refresh />
            </IconButton>
        </Box>
    );
};
